import { moveItemInArray } from '@angular/cdk/drag-drop';
import { computed, inject, Injectable, signal } from '@angular/core';
import { adminUserDtos } from '@vectronic/openApi-admin';
import { DialogService, LocalStorageService, TimeService } from '@vectronic/util';
import { of, tap } from 'rxjs';
import { EditColumnsComponent } from '../components/edit-columns/edit-columns.component';
import { DisplayColumn } from '../types/datatable-column';
import { DatatableConfig } from '../types/datatable-configuration';
import { DatatableColumnStorageService } from './datatable-column-storage.service';

@Injectable()
export class ColumnService<T extends object> {
  private timeService = inject(TimeService);
  private datatableColumnStorageService = inject(DatatableColumnStorageService, { optional: true });
  private localStorage = inject(LocalStorageService);
  private dialogService = inject(DialogService);

  private isDesktop = Boolean(window.desktop || window.adminDesktop);

  datatableConfig = signal<DatatableConfig<T>>(undefined);

  storedDisplayColumnIds = signal<number[]>([]);

  private storedDisplayColumns = computed(() => {
    const columns = this.datatableConfig()?.columns;
    const columnIds = this.storedDisplayColumnIds();
    if (columns?.length && columnIds?.length && columnIds.some((id) => id != null)) {
      return columnIds.map((id) => columns.find(({ columnId }) => columnId === id)?.fieldName);
    }
  });

  private defaultDisplayColumns = computed(() =>
    this.datatableConfig()
      ?.columns.filter(({ hide }) => !hide)
      .map(({ fieldName }) => fieldName as DisplayColumn<T>),
  );

  inputDisplayColumns = signal<DisplayColumn<T>[]>(undefined);
  isCompact = signal(false);

  private _pageSize = signal<number>(undefined);

  rawPageSize = computed(() => this._pageSize());

  setPageSize(size: number) {
    this._pageSize.set(size);
  }

  pageSize = computed(
    () =>
      this._pageSize() ??
      (this.datatableConfig()?.pagination as Exclude<DatatableConfig<T>['pagination'], false>)
        ?.defaultSize ??
      100,
  );

  displayColumns = computed(() => {
    if (this.datatableConfig()?.disableEditColumns) {
      return this.filterDisplayColumns(this.inputDisplayColumns() ?? this.defaultDisplayColumns());
    }
    let displayColumns =
      this.storedDisplayColumns() ?? this.inputDisplayColumns() ?? this.defaultDisplayColumns();
    if (!displayColumns?.length) displayColumns = this.defaultDisplayColumns();
    return this.filterDisplayColumns(displayColumns);
  });

  filterDisplayColumns(dcs: DisplayColumn<T>[]) {
    if (!this.timeService.shouldShowLocalTime())
      dcs = dcs?.filter((column) => !column?.includes('.lmt'));
    if (!this.isDesktop) dcs = dcs?.filter((column) => !column?.includes('.desktop'));
    return dcs?.filter((column) => column !== '#' && column !== 'rowActions' && column != null);
  }

  reorderColumns({ previousIndex, currentIndex }: { previousIndex: number; currentIndex: number }) {
    let columns = [...this.displayColumns()];
    columns = this.filterDisplayColumns(columns);
    moveItemInArray(columns, previousIndex, currentIndex);
    this.storedDisplayColumnIds.set(this.displayColumnsToIds(columns));
    this.storeTableSettings();
  }

  setColumnSelection(event: DisplayColumn<T>[]) {
    this.storedDisplayColumnIds.set(this.displayColumnsToIds(event));
    this.storeTableSettings();
  }

  displayColumnsToIds(fieldNames: DisplayColumn<T>[]) {
    return fieldNames?.map(
      (name) =>
        this.datatableConfig()?.columns.find(({ fieldName }) => fieldName === name)?.columnId,
    );
  }

  storeTableSettings() {
    const columnIds = this.displayColumnsToIds(this.displayColumns());
    if (!columnIds?.some((columnId) => columnId != null)) return;
    const { uniqueId } = this.datatableConfig();
    const data: adminUserDtos.DatatableSettings = {
      visibleColumns: columnIds,
      pageSize: this.pageSize(),
      compactMode: this.isCompact(),
    };
    if (this.datatableColumnStorageService) {
      this.datatableColumnStorageService.set(uniqueId, data).subscribe();
    } else {
      this.localStorage.setDatatableColumns(uniqueId, data);
    }
  }

  loadColumns(datatableConfig: DatatableConfig<T>, displayColumns?: DisplayColumn<T>[]) {
    this.datatableConfig.set(datatableConfig);
    if (displayColumns) this.inputDisplayColumns.set(displayColumns);
    const { uniqueId } = datatableConfig ?? {};
    if (!uniqueId) return of(null);
    const settings$ = this.datatableColumnStorageService
      ? this.datatableColumnStorageService.get(uniqueId)
      : of(this.localStorage.getDatatableColumns(uniqueId));
    return settings$.pipe(
      tap((stored) => {
        if (stored?.compactMode != undefined) this.isCompact.set(stored.compactMode);
        else this.isCompact.set(this.datatableConfig()?.compact ?? false);
        if (stored?.visibleColumns?.some((columnId) => columnId != null)) {
          this.storedDisplayColumnIds.set(Array.from(new Set(stored.visibleColumns)));
        }
        if (stored?.pageSize) this._pageSize.set(stored.pageSize);
      }),
    );
  }

  openEditColumnsModal() {
    let datatableColumns = this.datatableConfig()?.columns;
    if (!this.isDesktop) {
      datatableColumns = datatableColumns?.filter(
        ({ fieldName }) => !fieldName?.includes('.desktop'),
      );
    }
    this.dialogService.openWide(EditColumnsComponent<T>, {
      data: { shownColumns: this.displayColumns(), datatableColumns },
      afterOpened: ({ columnSelectionChanged }) =>
        columnSelectionChanged.subscribe((event) => this.setColumnSelection(event)),
    });
  }

  toggleCompactMode() {
    this.isCompact.set(!this.isCompact());
    this.storeTableSettings();
  }
}
